import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  Inject,
  inject,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BackendLogsService } from '../../../../services/backend-logs.service';
import { BackendLogDetail } from '../../../../interfaces/backend-log.interface';
import { DeleteIconButton } from '../../../../shared/components/delete-icon-button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog';

interface DialogData {
  logId: number;
}

@Component({
  selector: 'app-backend-log-detail-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DatePipe,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    DeleteIconButton,
  ],
  templateUrl: './backend-log-detail-dialog.component.html',
  styleUrls: ['./backend-log-detail-dialog.component.scss'],
})
export class BackendLogDetailDialogComponent {
  private destroyRef = inject(DestroyRef);

  detail: BackendLogDetail | null = null;
  isLoading = true;
  errorMessage: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<BackendLogDetailDialogComponent>,
    private logsService: BackendLogsService,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    this.loadDetail();
  }

  private loadDetail(): void {
    this.logsService
      .getLogDetail(this.data.logId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.detail = detail;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.errorMessage =
            err?.message || 'A log részletek betöltése sikertelen';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  formatBody(body: Record<string, any> | null | undefined): string {
    if (!body) {
      return '';
    }
    try {
      return JSON.stringify(body, null, 2);
    } catch {
      return String(body);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  deleteLog(): void {
    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Log törlése',
        message: 'Biztosan törölni szeretnéd ezt a log bejegyzést? A művelet nem vonható vissza.',
        type: 'delete',
      },
    });
    confirmRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.logsService
        .deleteLog(this.data.logId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.snackBar.open('Log bejegyzés törölve', 'OK', { duration: 3000 });
            this.dialogRef.close({ deleted: true });
          },
          error: () => {
            this.snackBar.open('Törlés sikertelen', 'OK', { duration: 3000 });
          },
        });
    });
  }
}
